import React from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { some } from 'lodash';

import dagstorp from 'LegacyWeployDesignLanguage/dagstorp';
import { Paper } from 'LegacyWeployDesignLanguage/Card';
import Avatar from 'LegacyWeployDesignLanguage/Avatar';
import { OverflowMenuButton, EmailIcon, PhoneIcon } from 'LegacyWeployDesignLanguage/icons';
import Fade from 'WeployDesignLanguage/transitions/Fade';
import Tooltip from 'WeployDesignLanguage/Tooltip';
import Menu, { MenuItem } from 'WeployDesignLanguage/Menu';
import Button from 'WeployDesignLanguage/buttons/Button';

import DetailWrapper from '../common/DetailWrapper';
import { CANCELLED, APPROVED } from '../../../containers/weployer/common/graphql/constants';

const Card = styled(Paper)`
  margin: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${props => props.theme.borderRadius.large};
  border: 1px solid ${props => props.theme.colors.gray};
  box-shadow: none;
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[1]} ${props => props.theme.spacing[1]};
  position: relative;
`;

const StyledPositionContent = styled.div`
  padding: 0 ${props => props.theme.spacing[1]};
`;

const AvatarWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const StyledNameWrapper = styled.div`
  text-align: center;
`;

const StyledName = styled.h6`
  display: inline-block;
  margin-bottom: ${props => (props.completeStatusVisible ? 0 : props.theme.spacing[2])};
  text-align: center;
  cursor: pointer;
`;

const StyledStatus = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const TimeWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing[1]};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledContactDetails = styled.span`
  color: ${props => props.theme.colors.text.primary};
`;

const IconWrapper = styled.div`
  color: ${props => props.theme.colors.text.primary};
  padding-right: ${props => props.theme.spacing[4]};
  display: inline;
`;

const StyledStatusWrapper = styled.div`
  ${props => props.theme.typography.caption}
  color: ${props => props.theme.colors.semantic.warning};
  font-weight: 700;
  margin: ${props => props.theme.spacing[1]} 0;
`;

const ButtonBar = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
`;

const StyledOverflowMenuContainer = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
`;

const StyledButtonsContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: flex-start;
`;

const PositionCard = ({
  onCancelClick,
  onExtendClick,
  onViewProfileClick,
  onTimesheetClick,
  onViewPastTimesheets,
  position,
  showApproveCallToAction,
  showCancelMenuItem,
  showExtendCallToAction,
  showPastTimesheetMenuItem,
  weployee,
}) => {
  const { name, profileImageUrl } = weployee;
  const {
    startTime,
    finishTime,
  } = position;
  const [menuAnchorElement, setMenuAnchorElement] = React.useState(null);
  const open = Boolean(menuAnchorElement);
  const completeStatusVisible = [CANCELLED, APPROVED].includes(position.status);
  const actionsVisible = some([showApproveCallToAction, showCancelMenuItem, showExtendCallToAction, showPastTimesheetMenuItem]);

  const handleMenuClick = event => (
    setMenuAnchorElement(event.currentTarget)
  );

  const handleMenuClose = () => (
    setMenuAnchorElement(null)
  );

  const renderExtendLink = () => {
    if (showExtendCallToAction) {
      return <Button appearance={Button.TEXT} onClick={() => onExtendClick(position.id)}>Extend</Button>;
    } else if (position.hasPendingExtensionRequests) {
      return <Button appearance={Button.TEXT} disabled>Extending</Button>;
    }

    return false;
  };

  const renderApproveLink = () => {
    if (showApproveCallToAction) {
      return (
        <Button appearance={Button.TEXT} onClick={() => onTimesheetClick(position.latestTimesheetId)}>
          Approve Timesheet
        </Button>
      );
    }
    return false;
  };

  const renderFinishedPositionStatuses = (positionStatus) => {
    switch (positionStatus) {
      case CANCELLED:
        return 'Cancelled';
      case APPROVED:
        return 'Completed';
      default:
        return null;
    }
  };

  const iconColor = dagstorp.colors.text.primary;

  return (
    <Card>
      <StyledPositionContent>
        <AvatarWrapper>
          <Avatar
            key={profileImageUrl}
            src={profileImageUrl}
            alt="Weployee's avatar"
            size={Avatar.EXTRA_SMALL}
          />
        </AvatarWrapper>
        <StyledNameWrapper>
          <StyledName
            completeStatusVisible={completeStatusVisible}
            onClick={() => onViewProfileClick(weployee.id)}
          >
            <Tooltip text="VIEW PROFILE">{name}</Tooltip>
          </StyledName>
        </StyledNameWrapper>
        <StyledStatus>
          {renderFinishedPositionStatuses(position.status)}
        </StyledStatus>
        <TimeWrapper>
          <div>{ `${moment(startTime).format('MMMM Do YYYY, h:mm a')} -`}</div>
          <div>{ `${moment(finishTime).format('MMMM Do YYYY, h:mm a')}` }</div>
        </TimeWrapper>
        <DetailWrapper>
          <IconWrapper>
            <EmailIcon color={iconColor} />
          </IconWrapper>
          <StyledContactDetails>
            { weployee.email }
          </StyledContactDetails>
        </DetailWrapper>
        <DetailWrapper>
          <IconWrapper>
            <PhoneIcon color={iconColor} />
          </IconWrapper>
          <StyledContactDetails>
            { weployee.phoneNumber }
          </StyledContactDetails>
        </DetailWrapper>
        {
          position.statusMessages.length > 0 &&
            <StyledStatusWrapper>
              {
                position.statusMessages.map(message => <div key={message}>{ message }</div>)
              }
            </StyledStatusWrapper>
        }
      </StyledPositionContent>
      { actionsVisible &&
        <ButtonBar>
          <StyledButtonsContainer>
            { renderExtendLink() }
            { renderApproveLink() }
          </StyledButtonsContainer>
            {
              (showCancelMenuItem || showPastTimesheetMenuItem) &&
                (
                  <StyledOverflowMenuContainer>
                    <Menu
                      id="fade-menu"
                      anchorEl={menuAnchorElement}
                      keepMounted
                      open={open}
                      onClose={handleMenuClose}
                      TransitionComponent={Fade}
                    >
                      {
                        showPastTimesheetMenuItem &&
                        <MenuItem onClick={() => onViewPastTimesheets(position.id)}>Past Timesheets</MenuItem>
                      }
                      {
                        showCancelMenuItem &&
                        <MenuItem onClick={() => onCancelClick(position.id)}>Cancel Weployee</MenuItem>
                      }
                    </Menu>
                    <OverflowMenuButton onClick={handleMenuClick} />
                  </StyledOverflowMenuContainer>
                )
            }
        </ButtonBar>
      }
    </Card>
  );
};

PositionCard.propTypes = {
  weployee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    profileImageUrl: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  position: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
    finishTime: PropTypes.string,
    weployeeId: PropTypes.string,
    jobId: PropTypes.string,
    statusMessages: PropTypes.arrayOf(PropTypes.string),
    timesheetIds: PropTypes.arrayOf(PropTypes.string),
    startTime: PropTypes.string,
    hasPendingExtensionRequests: PropTypes.bool,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    latestTimesheetId: PropTypes.string,
  }),
  showExtendCallToAction: PropTypes.bool,
  showPastTimesheetMenuItem: PropTypes.bool,
  showCancelMenuItem: PropTypes.bool,
  showApproveCallToAction: PropTypes.bool,
  onCancelClick: PropTypes.func.isRequired,
  onExtendClick: PropTypes.func.isRequired,
  onViewProfileClick: PropTypes.func.isRequired,
  onTimesheetClick: PropTypes.func.isRequired,
  onViewPastTimesheets: PropTypes.func.isRequired,
};

PositionCard.defaultProps = {
  weployee: {
    name: '',
    profileImageUrl: '',
  },
  position: {
    id: '',
    status: '',
    finishTime: '',
    weployeeId: '',
    jobId: '',
    statusMessages: [],
    timesheetIds: [],
  },
  showApproveCallToAction: false,
  showExtendCallToAction: false,
  showCancelMenuItem: false,
  showPastTimesheetMenuItem: false,
};

export default PositionCard;
